import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useAuth } from '@/hooks/useAuth';
import MfaSetupCard from '@/components/MfaSetupCard';
import { useToast } from '@/hooks/use-toast';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

import { Loader2, User, School, Bell, ArrowRight, BookOpen, CreditCard, Info, Trash2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import UsageMeter from '@/components/UsageMeter';
import { supabase } from '@/integrations/supabase/client';
import { useNotificationPreferences } from '@/hooks/useNotificationPreferences';
import { getUpgradeCTA } from '@/lib/upgrade';

export default function Settings() {
  const { profile, user, hasRole, refreshProfile, schoolPlan, planTier, planSubTier, billingStatus, signOut } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const isAdmin = hasRole('school_admin');
  const isSchoolTeacherOnly = planTier === 'school' && !isAdmin;

  const [displayName, setDisplayName] = useState(profile?.full_name ?? '');
  const [savingProfile, setSavingProfile] = useState(false);
  const { prefs, loading: prefsLoading, update: updatePref } = useNotificationPreferences();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Sync local state when profile loads or is refreshed
  useEffect(() => {
    setDisplayName(profile?.full_name ?? '');
  }, [profile?.full_name]);

  const handleSaveProfile = async () => {
    if (!user) return;
    setSavingProfile(true);
    const { data, error } = await supabase
      .from('profiles')
      .upsert({ user_id: user.id, email: user.email ?? null, full_name: displayName.trim() || null }, { onConflict: 'user_id' })
      .select('full_name')
      .single();
    setSavingProfile(false);
    if (error || !data) {
      toast({ title: 'Failed to update profile', description: error?.message ?? 'Unknown error', variant: 'destructive' });
    } else {
      await refreshProfile();
      toast({ title: 'Profile updated.' });
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') return;
    setDeleting(true);
    try {
      const { error } = await supabase.functions.invoke('delete-account');
      if (error) throw new Error(error.message);
      toast({ title: 'Account deleted', description: 'Your account and all associated data have been removed.' });
      // Auth user is already deleted server-side; signOut clears local state.
      // Ignore errors — the server session is gone regardless.
      await signOut().catch(() => supabase.auth.signOut({ scope: 'local' }));
      navigate('/');
    } catch (err: unknown) {
      toast({ title: 'Deletion failed', description: (err as Error).message, variant: 'destructive' });
      setDeleting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-2xl mx-auto space-y-6">
        <h1 className="text-3xl font-bold text-foreground">Settings</h1>

        {/* Profile */}
        <Card className="shadow-soft">
          <CardHeader>
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-lg">Profile</CardTitle>
            </div>
            <CardDescription>Update your display name.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="displayName">Display name</Label>
              <Input
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your name"
                disabled={savingProfile}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input value={user?.email ?? ''} disabled readOnly />
              <p className="text-xs text-muted-foreground">
                Email cannot be changed here. Contact support if needed.
              </p>
            </div>
            <Button
              onClick={handleSaveProfile}
              disabled={savingProfile || !displayName.trim()}
            >
              {savingProfile ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Profile
            </Button>
          </CardContent>
        </Card>

        {/* School — school-plan admins only */}
        {isAdmin && schoolPlan === 'school' && (
          <Card className="shadow-soft">
            <CardHeader>
              <div className="flex items-center gap-2">
                <School className="h-5 w-5 text-muted-foreground" />
                <CardTitle className="text-lg">School</CardTitle>
              </div>
              <CardDescription>
                Manage your school details, classes, and teachers.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild variant="outline">
                <Link to="/school-setup">
                  Open School Management
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* My Classes — individual plan only */}
        {schoolPlan === 'individual' && (
          <Card className="shadow-soft">
            <CardHeader>
              <div className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-muted-foreground" />
                <CardTitle className="text-lg">My Classes</CardTitle>
              </div>
              <CardDescription>
                Manage your year groups and classes.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild variant="outline">
                <Link to="/my-classes">
                  Open My Classes
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Plan & usage */}
        <Card className="shadow-soft">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-lg">Plan & Usage</CardTitle>
            </div>
            <CardDescription>
              {billingStatus === 'cancelled' || billingStatus === 'paused'
                ? 'Your subscription has been cancelled.'
                : planTier === 'free' ? 'You are on the Free plan.'
                : planTier === 'individual' ? 'You are on the Individual plan.'
                : planTier === 'school' ? 'You are on the School plan.'
                : planTier === 'trust' ? 'You are on the Trust plan.'
                : 'Your current plan.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {(billingStatus === 'cancelled' || billingStatus === 'paused') ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive space-y-3">
                <p>Your account is currently inactive. Reactivate your subscription to continue using WriteAssess.</p>
                <Button
                  size="sm"
                  onClick={() => navigate(planTier === 'school' ? '/school-billing' : '/individual-billing')}
                >
                  Reactivate Plan
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            ) : isSchoolTeacherOnly ? (
              <div className="flex items-start gap-3 rounded-md border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <p>Your school's plan and billing are managed by your school admin.</p>
              </div>
            ) : (
              <>
                {(planTier === 'free' || planTier === 'individual' || isAdmin) && (
                  <UsageMeter
                    planTier={planTier ?? null}
                    planSubTier={planSubTier ?? null}
                    isSchoolAdmin={isAdmin}
                  />
                )}
                <UpgradeButtons
                  planTier={planTier}
                  planSubTier={planSubTier ?? null}
                  schoolPlan={schoolPlan}
                  isAdmin={isAdmin}
                  navigate={navigate}
                  onManageSubscription={async () => {
                    try {
                      const { data: { session } } = await supabase.auth.getSession();
                      const { data, error } = await supabase.functions.invoke('stripe-portal', {
                        headers: { Authorization: `Bearer ${session?.access_token}` },
                      });
                      if (error || !data?.url) throw new Error(error?.message ?? 'Could not open portal');
                      window.location.href = data.url;
                    } catch (err: any) {
                      toast({ title: 'Error', description: err.message, variant: 'destructive' });
                    }
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card className="shadow-soft">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-lg">Notifications</CardTitle>
            </div>
            <CardDescription>
              Choose which emails you'd like to receive from WriteAssess.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {prefsLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading preferences…
              </div>
            ) : (
              <div className="space-y-0 divide-y">
                <NotifRow
                  title="Monthly assessment digest"
                  description="A summary of your assessment activity from the previous month, including grade breakdowns and submission counts."
                  checked={prefs.monthly_digest}
                  onCheckedChange={(v) => updatePref({ monthly_digest: v })}
                />
                <NotifRow
                  title="Billing & renewal reminders"
                  description="Alerts when your trial is ending, your subscription is due to renew, or a payment needs attention."
                  checked={prefs.billing_reminders}
                  onCheckedChange={(v) => updatePref({ billing_reminders: v })}
                />
                <NotifRow
                  title="Product updates"
                  description="Occasional emails about new features, improvements, and changes to WriteAssess."
                  checked={prefs.product_updates}
                  onCheckedChange={(v) => updatePref({ product_updates: v })}
                />
              </div>
            )}
          </CardContent>
        </Card>
        {/* Security */}
        <MfaSetupCard />

        {/* Danger zone */}
        <Card className="shadow-soft border-destructive/40">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              <CardTitle className="text-lg text-destructive">Danger Zone</CardTitle>
            </div>
            <CardDescription>
              Permanent actions that cannot be undone.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Delete account</p>
                <p className="text-xs text-muted-foreground">
                  Permanently removes your account, assessments, and all personal data.
                </p>
              </div>
              <Button variant="destructive" size="sm" onClick={() => { setDeleteConfirm(''); setDeleteDialogOpen(true); }}>
                Delete account
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete your account?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm text-muted-foreground">
            <p>This will permanently delete:</p>
            <ul className="list-disc pl-4 space-y-1">
              <li>Your profile and login credentials</li>
              <li>All writing submissions and AI assessments</li>
              <li>Your class and pupil records</li>
              <li>All associated data</li>
            </ul>
            <p className="text-destructive font-medium">This cannot be undone.</p>
            <div className="space-y-1.5 pt-1">
              <Label htmlFor="delete-confirm">
                Type <strong>DELETE</strong> to confirm
              </Label>
              <Input
                id="delete-confirm"
                value={deleteConfirm}
                onChange={e => setDeleteConfirm(e.target.value)}
                placeholder="DELETE"
                autoComplete="off"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={deleting || deleteConfirm !== 'DELETE'}
            >
              {deleting ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Deleting…</> : 'Delete my account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function UpgradeButtons({
  planTier,
  planSubTier,
  schoolPlan,
  isAdmin,
  navigate,
  onManageSubscription,
}: {
  planTier: import('@/lib/billing').PlanTier | null;
  planSubTier: string | null;
  schoolPlan: 'school' | 'individual' | null;
  isAdmin: boolean;
  navigate: (path: string) => void;
  onManageSubscription: () => void;
}) {
  const cta = getUpgradeCTA(planTier, planSubTier, schoolPlan);

  const manageBtn = planTier && planTier !== 'free' && isAdmin ? (
    <Button variant="outline" onClick={onManageSubscription}>
      Manage Subscription
    </Button>
  ) : null;

  if (!cta) return manageBtn;

  return (
    <div className="flex flex-wrap gap-3">
      <Button onClick={() => navigate(cta.primaryRoute)}>
        {cta.primaryLabel}
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
      {cta.secondaryLabel && cta.secondaryRoute && (
        <Button variant="outline" onClick={() => navigate(cta.secondaryRoute!)}>
          {cta.secondaryLabel}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      )}
      {manageBtn}
    </div>
  );
}

function NotifRow({
  title,
  description,
  checked,
  onCheckedChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-4">
      <div className="space-y-0.5">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground leading-relaxed max-w-sm">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} className="shrink-0 mt-0.5" />
    </div>
  );
}
